import { GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { useEffect, useMemo } from 'react'
import { CheckboxProps } from 'src/components/VirtualCardsBox/VirtualCardsBox'
import { BulkAction, useBulkAction } from 'src/hooks/bulkActionHooks'
import { VirtualCardListModel } from 'src/models/virtualCard.models'

export const CARD_SELECTION_LIMIT = 200

export const buildVisibleBulkSelection = ({
  visibleVirtualCards,
  selectedIds,
}: {
  visibleVirtualCards: VirtualCardListModel[]
  selectedIds: string[]
}): {
  selectableIds: string[]
  selectedVirtualCards: VirtualCardListModel[]
} => {
  const selectableIds = visibleVirtualCards
    .filter((virtualCard) => !virtualCard.isBillPay)
    .slice(0, CARD_SELECTION_LIMIT)
    .map((virtualCard) => virtualCard.id)
  const selectableIdSet = new Set(selectableIds)
  const selectedIdSet = new Set(selectedIds)
  const selectedVirtualCards = visibleVirtualCards.filter(
    (virtualCard) =>
      selectableIdSet.has(virtualCard.id) && selectedIdSet.has(virtualCard.id),
  )

  return {
    selectableIds,
    selectedVirtualCards,
  }
}

// MUI X v8 emits an `exclude` selection model for header select-all
// (`{ type: 'exclude', ids }` = every selectable row except `ids`). Resolving
// against `selectableIds` keeps that path from collapsing to an empty selection.
export const resolveGridSelectionIds = (
  selection: GridRowSelectionModel,
  selectableIds: string[],
): string[] => {
  const modelIds = new Set([...selection.ids].map((id) => id.toString()))
  if (selection.type === 'exclude') {
    return selectableIds.filter((id) => !modelIds.has(id))
  }
  return selectableIds.filter((id) => modelIds.has(id))
}

export type UseVirtualCardsBulkActionsArgs = {
  enabled: boolean
  visibleVirtualCards: VirtualCardListModel[]
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
  onSelectionLimit: () => void
  onSelectionAnalytics: (totalSelectedCards: number) => void
  onBulkAction: (selectedIds: string[]) => void
  onBulkActionAnalytics: (
    bulkAction: BulkAction | undefined,
    selectedVirtualCards: VirtualCardListModel[],
  ) => void
}

export type UseVirtualCardsBulkActionsResult = {
  checkboxProps?: CheckboxProps
  selectedVirtualCards: VirtualCardListModel[]
  handleBulkAction: (bulkAction?: BulkAction) => void
  clearSelection: () => void
}

const areStringIdsEqualAsSet = (left: string[], right: string[]): boolean => {
  if (left === right) return true
  if (left.length !== right.length) return false
  const leftSet = new Set(left)
  if (leftSet.size !== right.length) return false
  return right.every((id) => leftSet.has(id))
}

export const useVirtualCardsBulkActions = ({
  enabled,
  visibleVirtualCards,
  selectedIds,
  setSelectedIds,
  onSelectionLimit,
  onSelectionAnalytics,
  onBulkAction,
  onBulkActionAnalytics,
}: UseVirtualCardsBulkActionsArgs): UseVirtualCardsBulkActionsResult => {
  const { selectableIds, selectedVirtualCards } = useMemo(
    () =>
      buildVisibleBulkSelection({
        visibleVirtualCards,
        selectedIds,
      }),
    [selectedIds, visibleVirtualCards],
  )

  useEffect(() => {
    if (!enabled || selectedIds.length === 0) {
      return
    }

    const selectedVisibleIds = selectedIds.filter((id) =>
      selectableIds.includes(id),
    )

    if (!areStringIdsEqualAsSet(selectedVisibleIds, selectedIds)) {
      setSelectedIds(selectedVisibleIds)
    }
  }, [enabled, selectableIds, selectedIds, setSelectedIds])

  const { handleCheckboxClick, handleBulkAction } = useBulkAction(
    selectedIds,
    {
      updateSelectedIds: setSelectedIds,
      analyticsEvent: () => onSelectionAnalytics(selectedIds.length),
      limitCondition: selectedIds.length >= CARD_SELECTION_LIMIT,
      limitFunction: onSelectionLimit,
    },
    {
      bulkAction: onBulkAction,
      analyticsEvent: (bulkAction) =>
        onBulkActionAnalytics(bulkAction, selectedVirtualCards),
    },
  )

  if (!enabled) {
    return {
      selectedVirtualCards: [],
      handleBulkAction,
      clearSelection: () => undefined,
    }
  }

  const clearSelection = (): void => {
    onSelectionAnalytics(0)
    setSelectedIds([])
  }

  const handleBulkVirtualCardCheckboxClick = (): void => {
    if (selectedVirtualCards.length > 0) {
      clearSelection()
      return
    }

    onSelectionAnalytics(selectableIds.length)
    setSelectedIds(selectableIds)
  }

  const checkboxProps: CheckboxProps = {
    selectedVirtualCards,
    handleVirtualCardCheckboxClick: () => undefined,
    handleBulkVirtualCardCheckboxClick,
    handleDataGridVirtualCardCheckboxClick: (
      selection: GridRowSelectionModel,
    ) => {
      handleCheckboxClick({
        type: 'include',
        ids: new Set(resolveGridSelectionIds(selection, selectableIds)),
      })
    },
    total: visibleVirtualCards.length,
    canSelectVirtualCard: (virtualCard: VirtualCardListModel) =>
      !virtualCard.isBillPay,
  }

  return {
    checkboxProps,
    selectedVirtualCards,
    handleBulkAction,
    clearSelection,
  }
}
